import lodash from 'lodash'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { DEPOSIT_HISTORY_TYPE } from './consts'
import { TENANT_PROJECT_DEPOSIT_SYNC_ACTION, emitTenantProjectDepositSync, parseTenantProjectDepositSyncEvent } from './sync'

const DB_NUMERIC_AMOUNT = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/

export async function initTenantProjectDepositSync ({ getBuildingFloorZoneMap, routeId, addStorageListener }) {
  const { BuildingFloorZoneMap } = await getBuildingFloorZoneMap()
  addStorageListener()

  return {
    buildingOptions: Object.keys(BuildingFloorZoneMap),
    tenantProjectId: routeId,
  }
}

export async function reloadTenantProjectOnDepositSync ({ event, tenantProjectId, loadTenantProject }) {
  const syncData = parseTenantProjectDepositSyncEvent(
    event,
    tenantProjectId,
    Object.values(TENANT_PROJECT_DEPOSIT_SYNC_ACTION),
  )

  if (!syncData) {
    return false
  }

  await loadTenantProject(tenantProjectId)
  return true
}

export async function syncAllowContractorRefund ({ uploadDepositInfo, tenantProjectId }) {
  const result = await uploadDepositInfo(DEPOSIT_HISTORY_TYPE.allowContractorRefund)
  if (!result) {
    return null
  }

  emitTenantProjectDepositSync(tenantProjectId, TENANT_PROJECT_DEPOSIT_SYNC_ACTION.allowContractorRefund)
  return result
}

export async function syncNotifyPm ({
  validate,
  uploadInfo,
  notifyType,
  resetDirty,
  sendEmail,
  emailType,
  tenantProjectId,
  syncAction,
}) {
  if (!validate()) {
    return null
  }

  const result = await uploadInfo(notifyType)
  if (!result) {
    return null
  }

  resetDirty()
  await sendEmail(emailType)
  emitTenantProjectDepositSync(tenantProjectId, syncAction)
  return result
}

/** Preserve numeric/string zero; only null and empty string map to null. */
export function normalizeDepositAmount (amount) {
  if (amount == null || amount === '') {
    return null
  }
  return amount
}

export function isPersistableDepositRefundAmount (amount) {
  const value = String(amount == null ? '' : amount).trim()
  return !value || DB_NUMERIC_AMOUNT.test(value)
}

export function getLatestNotifyAccountingAmount (deposit) {
  const histories = lodash.orderBy(
    lodash.get(deposit, 'actionHistory', []).filter(item => item.actionType === DEPOSIT_HISTORY_TYPE.notifyAccounting),
    ['updatedAt'],
    ['desc'],
  )
  return histories.length ? +histories[0].amount : 0
}

export function buildDepositNotifyAccountingEmailPayload ({ deposit, amount, isDirect, payeeName, address, chequeFiles, currency, email }) {
  const histories = lodash.orderBy(
    lodash.get(deposit, 'actionHistory', []).filter(item => item.actionType === DEPOSIT_HISTORY_TYPE.notifyAccounting),
    ['updatedAt'],
    ['desc'],
  )
  const previousAmount = histories.length ? +histories[0].amount : 0
  const totalAmount = +(amount || 0)
  const differenceAmount = totalAmount - previousAmount
  const symbol = differenceAmount >= 0 ? '+' : '-'

  return {
    email,
    docIds: chequeFiles.map(item => item.id),
    deposit: {
      amount: amount || 0,
      isDirect,
      payeeName,
      address,
    },
    difference: `${symbol}${currency}${Math.abs(differenceAmount)}`,
    previous: `${currency}${previousAmount}`,
    total: `${currency}${totalAmount}`,
  }
}

export function buildRefundNotifyAccountingEmailPayload ({ deposit, amount, creditTo, isDirect, payeeName, address, email }) {
  return {
    email,
    deposit,
    refund: {
      amount: amount || 0,
      creditTo,
      isDirect,
      payeeName,
      address,
    },
  }
}

export async function syncDepositNotifyAccounting ({
  validateAccounting,
  buildEmails,
  uploadDepositInfo,
  resetDirty,
  closeModal,
  tenantProjectId,
  emailType,
  requestHandler,
  emailPayload,
}) {
  if (!validateAccounting()) {
    return null
  }

  const [notifyAccountingEmail] = await buildEmails(emailType, emailPayload)
  const result = await uploadDepositInfo(DEPOSIT_HISTORY_TYPE.notifyAccounting, {
    extraRequest: {
      email: notifyAccountingEmail.mailRequest,
    },
    requestHandler,
  })
  if (!result) {
    return null
  }

  resetDirty()
  emitTenantProjectDepositSync(tenantProjectId, TENANT_PROJECT_DEPOSIT_SYNC_ACTION.depositNotifyAccounting)
  closeModal()
  return result
}

export async function syncRefundNotifyAccounting ({
  validateAccounting,
  buildEmails,
  uploadRefundInfo,
  resetDirty,
  closeModal,
  tenantProjectId,
  emailType,
  requestHandler,
  email,
  emailPayload,
}) {
  if (!validateAccounting()) {
    return null
  }

  const [notifyAccountingEmail] = await buildEmails(emailType, emailPayload || { email })
  const result = await uploadRefundInfo(DEPOSIT_HISTORY_TYPE.notifyAccounting, {
    extraRequest: {
      email: notifyAccountingEmail.mailRequest,
    },
    requestHandler,
  })
  if (!result) {
    return null
  }

  resetDirty()
  emitTenantProjectDepositSync(tenantProjectId, TENANT_PROJECT_DEPOSIT_SYNC_ACTION.refundNotifyAccounting)
  closeModal()
  return result
}

export async function syncResumeRefundRequest ({ uploadRefundInfo, tenantProjectId, validate }) {
  if (validate && !validate()) {
    return null
  }

  const result = await uploadRefundInfo(DEPOSIT_HISTORY_TYPE.resumeRefundRequest)
  if (!result) {
    return null
  }

  emitTenantProjectDepositSync(tenantProjectId, TENANT_PROJECT_DEPOSIT_SYNC_ACTION.resumeRefundRequest)
  return result
}

export async function syncManagementFeeNotifyAccounting ({
  uploadManagementInfo,
  resetDirty,
  sendEmail,
  closeModal,
  tenantProjectId,
  emailType,
  email,
}) {
  const result = await uploadManagementInfo(DEPOSIT_HISTORY_TYPE.notifyAccounting)
  if (!result) {
    return null
  }

  resetDirty()
  await sendEmail(emailType, { email })
  closeModal()
  emitTenantProjectDepositSync(tenantProjectId, TENANT_PROJECT_DEPOSIT_SYNC_ACTION.managementFeeNotifyAccounting)
  return result
}

export function isSaveFieldModified (recordValue, localValue) {
  if (recordValue === localValue) {
    return false
  }
  if (
    (recordValue == null && localValue === 0) ||
    (localValue == null && recordValue === 0)
  ) {
    return false
  }
  if (typeof recordValue === 'boolean' || typeof localValue === 'boolean') {
    return Boolean(recordValue) !== Boolean(localValue)
  }
  return String(recordValue == null ? '' : recordValue) !== String(localValue == null ? '' : localValue)
}

export function getModifiedSaveFields (record, local, fields) {
  return fields.filter(field => isSaveFieldModified(lodash.get(record, field), local[field]))
}

export function pickSaveFieldValues (fields, source, transforms = {}) {
  return fields.reduce((values, field) => {
    const raw = lodash.get(source, field)
    const transform = transforms[field]
    values[field] = transform ? transform(raw, source) : raw
    return values
  }, {})
}

export function validateModifiedSaveFields ({
  record,
  local,
  fields,
  fieldValidators = {},
  groupValidators = {},
  context = {},
  finalCheck,
}) {
  const modifiedFields = getModifiedSaveFields(record, local, fields)
  if (!modifiedFields.length) {
    return true
  }

  const validatedGroups = new Set()

  for (const field of modifiedFields) {
    const fieldValidator = fieldValidators[field]
    if (!fieldValidator) {
      continue
    }

    if (fieldValidator.group) {
      const groupName = fieldValidator.group
      if (validatedGroups.has(groupName)) {
        continue
      }
      const groupValidator = groupValidators[groupName]
      if (!groupValidator) {
        continue
      }
      if (groupValidator.when && !groupValidator.when(context)) {
        continue
      }
      validatedGroups.add(groupName)
      if (!runSaveFieldValidation(groupValidator)) {
        return false
      }
      continue
    }

    if (fieldValidator.when && !fieldValidator.when(context)) {
      continue
    }
    if (!runSaveFieldValidation(fieldValidator)) {
      return false
    }
  }

  return finalCheck ? finalCheck(context) : true
}

function runSaveFieldValidation (validator) {
  fusionInputCheckUtil.validateAllFields({ allowEmpty: 'all' }, validator.inputSelector)
  const validSelector = validator.validSelector || validator.inputSelector
  return fusionInputCheckUtil.isAllFieldsValid(validSelector)
}

export function buildDirtyFieldSaveRequest ({
  record,
  local,
  fields,
  currentUser,
  actionType,
}) {
  const modifiedFields = getModifiedSaveFields(record, local, fields)
  if (!modifiedFields.length) {
    return null
  }

  const request = {
    updatedAt: new Date(),
    updatedBy: currentUser.id,
  }

  modifiedFields.forEach(field => {
    request[field] = local[field]
  })

  const actionHistory = lodash.cloneDeep(record.actionHistory || [])
  const historyEntry = {
    actionType,
    user: {
      id: currentUser.id,
      username: currentUser.username,
      displayName: currentUser.displayName,
    },
    isNewRec: true,
  }
  if (modifiedFields.includes('amount')) {
    historyEntry.amount = local.amount != null && local.amount !== '' ? local.amount : null
  }
  actionHistory.unshift(historyEntry)
  request.actionHistory = actionHistory

  return request
}

export async function syncSaveDepositAmount ({
  uploadSaveInfo,
  hasDirtySaveFields,
  resetDirty,
  validate,
}) {
  if (!hasDirtySaveFields()) {
    return true
  }

  if (validate && !validate()) {
    return null
  }

  const result = await uploadSaveInfo()
  if (!result) {
    return null
  }

  resetDirty()
  return result
}

/**
 * Save & Continue for multiple deposit/refund sections without reloading between uploads.
 */
export async function syncSaveDepositAmountBatch ({
  components,
  reloadProject,
  resetDirty,
  onSaveSuccess,
}) {
  const dirtyComponents = components.filter(component => component.hasDirtySaveFields())
  if (!dirtyComponents.length) {
    return true
  }

  for (const component of dirtyComponents) {
    if (component.validateAmountBeforeSave && !component.validateAmountBeforeSave()) {
      return null
    }
  }

  for (const component of dirtyComponents) {
    const result = await component.uploadSaveAndContinueInfo({ skipReload: true })
    if (result === null) {
      return null
    }
  }

  await reloadProject()
  resetDirty()
  if (onSaveSuccess) {
    onSaveSuccess(dirtyComponents)
  }
  return true
}
